import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { signalRService, SignalRCallbacks } from '../services/signalRService';
import { useUser } from './UserContext';
import { useToast } from './ToastContext';
import { authStorage } from '../api/authApi';

interface SignalRContextType {
  isConnected: boolean;
  connectionId?: string;
  onlineUsers: string[];
  joinListGroup: (listId: number) => Promise<void>;
  leaveListGroup: (listId: number) => Promise<void>;
  viewingTask: (taskId: number) => Promise<void>;
  stopViewingTask: (taskId: number) => Promise<void>;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

interface SignalRProviderProps {
  children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const { currentUser, isInitialized } = useUser();
  const queryClient = useQueryClient();
  const { showSuccess, showInfo } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState<string>();
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const isConnectingRef = useRef(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Clear any pending connection attempts
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = undefined;
    }

    // Don't attempt connection until user context is initialized
    if (!isInitialized) {
      return;
    }

    if (!currentUser) {
      isConnectingRef.current = false;
      // Disconnect when user logs out
      signalRService.disconnect().then(() => {
        setIsConnected(false);
        setConnectionId(undefined);
        setOnlineUsers([]);
      });
      return;
    }

    // Prevent multiple simultaneous connections
    if (isConnectingRef.current) {
      return;
    }

    const token = authStorage.getToken();
    
    if (!token) {
      return;
    }

    // Validate token format and contents
    let email: string;
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('Invalid JWT token format');
        return;
      }
      
      // Decode the payload to check for email claim
      const payload = JSON.parse(atob(tokenParts[1]));
      
      email = payload.email || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'];
      if (!email) {
        console.error('No email claim found in token');
        return;
      }
      
      // Check if token is expired
      const now = Date.now() / 1000;
      if (payload.exp && payload.exp < now) {
        console.error('Token is expired');
        return;
      }
      
    } catch (error) {
      console.error('Error validating token:', error);
      return;
    }

    // Connect to SignalR with a stable async function
    const connectSignalR = async () => {
      try {
        // Set up SignalR callbacks with stable references
        const callbacks: SignalRCallbacks = {
          onTaskCreated: () => {
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
          },

          onTaskUpdated: () => {
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
          },

          onTaskDeleted: () => {
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
          },

          onTaskCompleted: (data) => {
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
            
            if (data.Task && data.ChangedBy !== currentUser.email) {
              showSuccess(`"${data.Task.title}" was completed by ${data.ChangedBy}`);
            }
          },

          onTaskAssigned: (data) => {
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
            
            if (data.AssignedTo === currentUser.email) {
              showInfo(`You have been assigned to task: ${data.Task.title}`);
            }
          },

          onListCreated: () => {
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
          },

          onListUpdated: () => {
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
          },

          onListDeleted: () => {
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
          },

          onListShared: (data) => {
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
            
            if (data.SharedWith === currentUser.email) {
              showSuccess(`List "${data.List.name}" has been shared with you by ${data.ChangedBy}`);
            }
          },

          onCommentAdded: () => {
            // Always invalidate queries regardless of payload structure
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['todolists', currentUser.email] });
            
            // Also force refetch to ensure data is updated
            queryClient.refetchQueries({ queryKey: ['todos', currentUser.email] });
          },

          onNewNotification: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['unreadCount', currentUser.email] });
            showInfo('New notification received');
          },

          onUnreadCountUpdated: (data) => {
            queryClient.setQueryData(['unreadCount', currentUser.email], data.UnreadCount);
          },

          onUserViewingTask: () => {
            // User viewing task event
          },

          onUserStoppedViewingTask: () => {
            // User stopped viewing task event
          },

          onUserOnlineStatusChanged: (data) => {
            setOnlineUsers(prev => {
              if (data.IsOnline) {
                return prev.includes(data.UserEmail) ? prev : [...prev, data.UserEmail];
              } else {
                return prev.filter(email => email !== data.UserEmail);
              }
            });
          },

          onOnlineUsersUpdated: (onlineUsers) => {
            setOnlineUsers(onlineUsers);
          },

          onInvitationReceived: (data) => {
            queryClient.invalidateQueries({ queryKey: ['receivedInvitations', currentUser.email] });
            showSuccess(`New invitation from ${data.InviterName}!`);
          },

          onInvitationStatusChanged: (data) => {
            // Invalidate both sent and received invitations since invitations are removed after processing
            queryClient.invalidateQueries({ queryKey: ['sentInvitations', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['receivedInvitations', currentUser.email] });
            
            // Also invalidate contacts since accepted invitations create new contacts
            if (data.status === 'accepted') {
              queryClient.invalidateQueries({ queryKey: ['contacts', currentUser.email] });
              
              // Show different messages based on whether current user is inviter or invitee
              if (data.inviteeEmail === currentUser.email) {
                // Current user accepted someone's invitation
                showSuccess('Invitation accepted successfully!');
              } else {
                // Someone accepted current user's invitation
                showSuccess(`${data.inviteeEmail} accepted your invitation!`);
              }
            } else if (data.status === 'declined') {
              // Show different messages based on whether current user is inviter or invitee
              if (data.inviteeEmail === currentUser.email) {
                // Current user declined someone's invitation
                showSuccess('Invitation declined');
              } else {
                // Someone declined current user's invitation
                showInfo(`${data.inviteeEmail} declined your invitation.`);
              }
            }
          },

          // Kanban event handlers
          onTaskStatusChanged: (data) => {
            // Invalidate todos and Kanban config to refresh UI
            queryClient.invalidateQueries({ queryKey: ['todos', currentUser.email] });
            queryClient.invalidateQueries({ queryKey: ['kanban-config', data.ListId] });
          },

          onKanbanColumnsUpdated: (data) => {
            // Update the Kanban config cache directly
            queryClient.setQueryData(['kanban-config', data.ListId], data.Config);
            // Also invalidate to ensure fresh data
            queryClient.invalidateQueries({ queryKey: ['kanban-config', data.ListId] });
          },

          onUserMovingTask: (data) => {
            // Could show UI indicators for collaborative dragging
            console.log('User moving task:', data);
          },

          onUserStoppedMovingTask: (data) => {
            // Clean up any UI indicators
            console.log('User stopped moving task:', data);
          }
        };

        signalRService.setCallbacks(callbacks);

        // Connect to SignalR only if not already connected
        const currentStatus = signalRService.getConnectionStatus();
        if (currentStatus.isConnected) {
          setIsConnected(true);
          setConnectionId(currentStatus.connectionId);
          setOnlineUsers([]); // Reset online users and request fresh list
          signalRService.getOnlineUsers();
          return;
        }

        // Set connecting flag
        isConnectingRef.current = true;

        // Connect to SignalR
        await signalRService.connect(token);
        
        const status = signalRService.getConnectionStatus();
        setIsConnected(status.isConnected);
        setConnectionId(status.connectionId);
        
        // Request initial list of online users
        if (status.isConnected) {
          // Add a small delay to ensure connection is fully established
          setTimeout(() => {
            signalRService.getOnlineUsers();
          }, 500);
        }
      } catch (error) {
        console.error('Failed to connect to SignalR:', error);
        setIsConnected(false);
        setConnectionId(undefined);
        setOnlineUsers([]);
      } finally {
        isConnectingRef.current = false;
      }
    };

    // Give a small delay to ensure user state is fully settled after login
    connectionTimeoutRef.current = setTimeout(() => {
      connectSignalR();
    }, 250);

    // Cleanup function
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = undefined;
      }
      
      // Only disconnect if user is logging out (not just re-rendering)
      if (!currentUser && isInitialized) {
        isConnectingRef.current = false;
        signalRService.disconnect().then(() => {
          setIsConnected(false);
          setConnectionId(undefined);
          setOnlineUsers([]);
        });
      }
    };
  }, [currentUser?.email, isInitialized]); // Depend on both email and initialization state

  const joinListGroup = async (listId: number) => {
    await signalRService.joinListGroup(listId);
  };

  const leaveListGroup = async (listId: number) => {
    await signalRService.leaveListGroup(listId);
  };

  const viewingTask = async (taskId: number) => {
    await signalRService.viewingTask(taskId);
  };

  const stopViewingTask = async (taskId: number) => {
    await signalRService.stopViewingTask(taskId);
  };

  const value: SignalRContextType = {
    isConnected,
    connectionId,
    onlineUsers,
    joinListGroup,
    leaveListGroup,
    viewingTask,
    stopViewingTask
  };

  return (
    <SignalRContext.Provider value={value}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = (): SignalRContextType => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
};

// Safe hook that doesn't throw on missing context
export const useSignalRSafe = (): SignalRContextType | null => {
  const context = useContext(SignalRContext);
  return context;
};
